/**
 * Pre-Pricing Package Manager
 * 
 * Admin page for viewing/editing all pre-pricing packages and job templates.
 * Supports editing labour lines, parts, consumables, notes, and creating new packages.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Search, Package, Wrench, Check, X, Pencil, ChevronDown, ChevronRight, Clock, Tag, Activity } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LABOUR_CATEGORIES = [
  { value: 'strip_renew', label: 'Strip & Renew' },
  { value: 'service_labour', label: 'Service Labour' },
  { value: 'inspection_testing', label: 'Inspection & Testing' },
  { value: 'wheel_alignment', label: 'Wheel Alignment' },
  { value: 'diagnostic', label: 'Diagnostic' },
  { value: 'welding_fabrication', label: 'Welding & Fabrication' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'bodywork', label: 'Bodywork' },
];

const SOURCE_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'package', label: 'Packages' },
  { value: 'template', label: 'Templates' },
];

export default function PricingPackageAdmin() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [editingPkg, setEditingPkg] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pkgRes, catRes] = await Promise.all([
        fetch(`${API_URL}/api/pricing-packages?include_inactive=true`),
        fetch(`${API_URL}/api/pricing-packages/categories`),
      ]);
      if (pkgRes.ok) setPackages(await pkgRes.json());
      if (catRes.ok) setCategories(await catRes.json());
    } catch (err) {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = packages.filter(p => {
    if (sourceFilter !== 'all' && p.source_type !== sourceFilter) return false;
    if (categoryFilter !== 'all' && p.category !== categoryFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        (p.name || '').toLowerCase().includes(q) ||
        (p.template_key || '').toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  const handleSave = async (pkg) => {
    try {
      const res = await fetch(`${API_URL}/api/pricing-packages/${pkg.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pkg),
      });
      if (!res.ok) throw new Error('Save failed');
      toast.success(`Saved: ${pkg.name}`);
      setEditingPkg(null);
      fetchData();
    } catch {
      toast.error('Failed to save package');
    }
  };

  const handleCreate = async (pkg) => {
    try {
      const res = await fetch(`${API_URL}/api/pricing-packages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pkg),
      });
      if (!res.ok) throw new Error('Create failed');
      toast.success(`Created: ${pkg.name}`);
      setShowCreate(false);
      fetchData();
    } catch {
      toast.error('Failed to create package');
    }
  };

  const handleToggleActive = async (pkg) => {
    try {
      const res = await fetch(`${API_URL}/api/pricing-packages/${pkg.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !pkg.is_active }),
      });
      if (!res.ok) throw new Error('Toggle failed');
      toast.success(pkg.is_active ? 'Deactivated' : 'Activated');
      fetchData();
    } catch {
      toast.error('Failed to update');
    }
  };

  if (editingPkg) {
    return <PackageEditor pkg={editingPkg} onSave={handleSave} onCancel={() => setEditingPkg(null)} />;
  }

  if (showCreate) {
    return <PackageEditor pkg={null} onSave={handleCreate} onCancel={() => setShowCreate(false)} />;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="pricing-package-admin">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-gray-100 rounded" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Pre-Pricing Packages</h1>
              <p className="text-xs text-gray-500">{packages.length} packages & templates</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
            data-testid="create-package-btn"
          >
            <Plus className="w-4 h-4" /> New Package
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-4 py-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search packages..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm border rounded-md"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-1.5">
            {SOURCE_FILTERS.map(f => (
              <button
                key={f.value}
                onClick={() => setSourceFilter(f.value)}
                className={`px-3 py-1.5 text-xs rounded-full border ${
                  sourceFilter === f.value
                    ? 'bg-blue-50 border-blue-300 text-blue-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
                data-testid={`filter-${f.value}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <select
            value={categoryFilter}
            onChange={e => setCategoryFilter(e.target.value)}
            className="px-3 py-1.5 text-xs border rounded-md bg-white"
            data-testid="category-filter"
          >
            <option value="all">All Categories</option>
            {categories.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </div>

      {/* List */}
      <div className="max-w-6xl mx-auto px-4 pb-8">
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">No packages match filters</div>
        ) : (
          <div className="space-y-2">
            {filtered.map(pkg => (
              <PackageRow
                key={pkg.id}
                pkg={pkg}
                isExpanded={expandedId === pkg.id}
                onToggle={() => setExpandedId(expandedId === pkg.id ? null : pkg.id)}
                onEdit={() => setEditingPkg({...pkg})}
                onToggleActive={() => handleToggleActive(pkg)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ============== Package Row (List Item) ==============

function PackageRow({ pkg, isExpanded, onToggle, onEdit, onToggleActive }) {
  const labourCount = pkg.labour_lines?.length || 0;
  const partsCount = pkg.parts_lines?.length || 0;
  const totalHours = pkg.labour_lines?.reduce((s, l) => s + (l.hours || 0), 0) || 0;

  return (
    <div className={`bg-white border rounded-lg overflow-hidden ${!pkg.is_active ? 'opacity-60' : ''}`} data-testid={`package-row-${pkg.id}`}>
      <div className="flex items-center px-4 py-3 cursor-pointer hover:bg-gray-50" onClick={onToggle}>
        <div className="mr-3">
          {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm text-gray-900 truncate">{pkg.name}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
              pkg.source_type === 'package'
                ? 'bg-purple-50 text-purple-700 border border-purple-200'
                : 'bg-teal-50 text-teal-700 border border-teal-200'
            }`}>
              {pkg.source_type === 'package' ? 'Package' : 'Template'}
            </span>
            {!pkg.is_active && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">Inactive</span>
            )}
            {pkg.is_custom && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Custom</span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
            <span className="flex items-center gap-1"><Tag className="w-3 h-3" />{pkg.category}</span>
            {labourCount > 0 && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{totalHours.toFixed(1)} hrs</span>}
            <span className="flex items-center gap-1"><Wrench className="w-3 h-3" />{partsCount} parts</span>
            {pkg.template_key && <span className="text-gray-400">key: {pkg.template_key}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-4" onClick={e => e.stopPropagation()}>
          <button
            onClick={onEdit}
            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
            data-testid={`edit-btn-${pkg.id}`}
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={onToggleActive}
            className={`px-2 py-1 text-xs rounded border ${
              pkg.is_active
                ? 'text-gray-500 border-gray-200 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
                : 'text-green-600 border-green-200 hover:bg-green-50'
            }`}
            data-testid={`toggle-active-${pkg.id}`}
          >
            {pkg.is_active ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </div>

      {/* Expanded preview */}
      {isExpanded && (
        <div className="border-t bg-gray-50/50 px-4 py-3">
          <div className="grid grid-cols-3 gap-4 text-xs">
            {/* Labour */}
            <div>
              <h4 className="font-medium text-gray-700 mb-1.5 uppercase tracking-wide text-[10px]">Labour</h4>
              {pkg.labour_lines?.length > 0 ? (
                <div className="space-y-1">
                  {pkg.labour_lines.map((l, i) => (
                    <div key={i} className="flex justify-between text-gray-600">
                      <span className="truncate mr-2">{l.description}</span>
                      <span className="text-gray-400 whitespace-nowrap">{l.hours}h</span>
                    </div>
                  ))}
                </div>
              ) : <span className="text-gray-400">None</span>}
            </div>
            {/* Parts */}
            <div>
              <h4 className="font-medium text-gray-700 mb-1.5 uppercase tracking-wide text-[10px]">Parts</h4>
              {pkg.parts_lines?.length > 0 ? (
                <div className="space-y-1">
                  {pkg.parts_lines.map((p, i) => (
                    <div key={i} className="flex justify-between text-gray-600">
                      <span className="truncate mr-2">{p.description}</span>
                      <span className="text-gray-400 whitespace-nowrap">x{p.quantity}</span>
                    </div>
                  ))}
                </div>
              ) : <span className="text-gray-400">None</span>}
            </div>
            {/* Notes + Consumables */}
            <div>
              <h4 className="font-medium text-gray-700 mb-1.5 uppercase tracking-wide text-[10px]">Notes</h4>
              {pkg.notes?.length > 0 ? (
                <div className="space-y-1">
                  {pkg.notes.map((n, i) => (
                    <div key={i} className="text-gray-600">{n.text || n}</div>
                  ))}
                </div>
              ) : <span className="text-gray-400">None</span>}
              {pkg.consumables?.length > 0 && (
                <>
                  <h4 className="font-medium text-gray-700 mt-2 mb-1.5 uppercase tracking-wide text-[10px]">Consumables</h4>
                  {pkg.consumables.map((c, i) => (
                    <div key={i} className="text-gray-600">{c.description}</div>
                  ))}
                </>
              )}
            </div>
          </div>
          <button
            onClick={e => { e.stopPropagation(); }}
            className="mt-3 text-xs text-blue-600 hover:underline"
          >
          </button>
        </div>
      )}
    </div>
  );
}


// ============== Package Editor (Full Page) ==============

function PackageEditor({ pkg, onSave, onCancel }) {
  const isNew = !pkg;
  const [form, setForm] = useState(pkg || {
    name: '',
    source_type: 'package',
    category: 'General',
    description: '',
    template_key: '',
    customer_facing_name: '',
    labour_lines: [],
    parts_lines: [],
    consumables: [],
    notes: [],
    linked_package_id: '',
    is_active: true,
  });

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const addLabour = () => update('labour_lines', [
    ...form.labour_lines,
    { id: Date.now().toString(36), description: '', category: 'strip_renew', hours: 1.0 }
  ]);
  const removeLabour = (idx) => update('labour_lines', form.labour_lines.filter((_, i) => i !== idx));
  const updateLabour = (idx, field, val) => {
    const updated = [...form.labour_lines];
    updated[idx] = { ...updated[idx], [field]: val };
    update('labour_lines', updated);
  };

  const addPart = () => update('parts_lines', [
    ...form.parts_lines,
    { id: Date.now().toString(36), description: '', quantity: 1, unit_price: 0, is_consumable: false, is_mandatory: false }
  ]);
  const removePart = (idx) => update('parts_lines', form.parts_lines.filter((_, i) => i !== idx));
  const updatePart = (idx, field, val) => {
    const updated = [...form.parts_lines];
    updated[idx] = { ...updated[idx], [field]: val };
    update('parts_lines', updated);
  };

  const addConsumable = () => update('consumables', [
    ...form.consumables,
    { id: Date.now().toString(36), description: '' }
  ]);
  const removeConsumable = (idx) => update('consumables', form.consumables.filter((_, i) => i !== idx));
  const updateConsumable = (idx, val) => {
    const updated = [...form.consumables];
    updated[idx] = { ...updated[idx], description: val };
    update('consumables', updated);
  };

  const addNote = () => update('notes', [
    ...form.notes,
    { text: '', note_type: 'info' }
  ]);
  const removeNote = (idx) => update('notes', form.notes.filter((_, i) => i !== idx));
  const updateNote = (idx, field, val) => {
    const updated = [...form.notes];
    updated[idx] = { ...updated[idx], [field]: val };
    update('notes', updated);
  };

  const handleSubmit = () => {
    if (!form.name.trim()) {
      toast.error('Package name is required');
      return;
    }
    onSave(form);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="package-editor">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onCancel} className="p-1.5 hover:bg-gray-100 rounded" data-testid="editor-cancel-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">
              {isNew ? 'Create New Package' : `Edit: ${form.name}`}
            </h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50"
              data-testid="editor-discard-btn"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
              data-testid="editor-save-btn"
            >
              <Check className="w-4 h-4" /> {isNew ? 'Create' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Basic Info */}
        <section className="bg-white border rounded-lg p-5">
          <h2 className="text-sm font-semibold text-gray-800 mb-4">Basic Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Package Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => update('name', e.target.value)}
                className="w-full px-3 py-2 text-sm border rounded-md"
                placeholder="e.g. Timing Belt Kit Replacement"
                data-testid="pkg-name"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Customer-Facing Name</label>
              <input
                type="text"
                value={form.customer_facing_name || ''}
                onChange={e => update('customer_facing_name', e.target.value)}
                className="w-full px-3 py-2 text-sm border rounded-md"
                placeholder="Name shown to customers"
                data-testid="pkg-customer-name"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Category</label>
              <input
                type="text"
                value={form.category}
                onChange={e => update('category', e.target.value)}
                className="w-full px-3 py-2 text-sm border rounded-md"
                placeholder="e.g. Brakes, Engine, Suspension"
                data-testid="pkg-category"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Type</label>
              <select
                value={form.source_type}
                onChange={e => update('source_type', e.target.value)}
                className="w-full px-3 py-2 text-sm border rounded-md bg-white"
                data-testid="pkg-source-type"
              >
                <option value="package">Package</option>
                <option value="template">Template</option>
              </select>
            </div>
          </div>
          {form.source_type === 'template' && (
            <div className="mt-4">
              <label className="block text-xs text-gray-600 mb-1">Template Key (used for VI item mapping)</label>
              <input
                type="text"
                value={form.template_key || ''}
                onChange={e => update('template_key', e.target.value)}
                className="w-full px-3 py-2 text-sm border rounded-md font-mono"
                placeholder="e.g. front_brake_discs"
                data-testid="pkg-template-key"
              />
            </div>
          )}
          <div className="mt-4">
            <label className="block text-xs text-gray-600 mb-1">Description</label>
            <textarea
              value={form.description || ''}
              onChange={e => update('description', e.target.value)}
              className="w-full px-3 py-2 text-sm border rounded-md"
              rows={2}
              placeholder="Brief description of this package"
              data-testid="pkg-description"
            />
          </div>
        </section>

        {/* Labour Lines */}
        <section className="bg-white border rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-800">Labour Lines</h2>
            <button onClick={addLabour} className="flex items-center gap-1 text-xs text-blue-600 hover:underline" data-testid="add-labour-btn">
              <Plus className="w-3.5 h-3.5" /> Add Labour
            </button>
          </div>
          {form.labour_lines.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No labour lines — click "Add Labour" above</p>
          ) : (
            <div className="space-y-2">
              {form.labour_lines.map((l, idx) => (
                <div key={l.id || idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded" data-testid={`labour-row-${idx}`}>
                  <input
                    type="text"
                    value={l.description}
                    onChange={e => updateLabour(idx, 'description', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-sm border rounded"
                    placeholder="Labour description"
                  />
                  <select
                    value={l.category}
                    onChange={e => updateLabour(idx, 'category', e.target.value)}
                    className="px-2 py-1.5 text-xs border rounded bg-white w-36"
                  >
                    {LABOUR_CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  <div className="flex items-center gap-1 w-20">
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={l.hours}
                      onChange={e => updateLabour(idx, 'hours', parseFloat(e.target.value) || 0)}
                      className="w-14 px-2 py-1.5 text-sm border rounded text-center"
                    />
                    <span className="text-xs text-gray-400">hrs</span>
                  </div>
                  <button onClick={() => removeLabour(idx)} className="p-1 text-gray-400 hover:text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Parts Lines */}
        <section className="bg-white border rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-800">Parts</h2>
            <button onClick={addPart} className="flex items-center gap-1 text-xs text-blue-600 hover:underline" data-testid="add-part-btn">
              <Plus className="w-3.5 h-3.5" /> Add Part
            </button>
          </div>
          {form.parts_lines.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No parts — click "Add Part" above</p>
          ) : (
            <div className="space-y-2">
              {form.parts_lines.map((p, idx) => (
                <div key={p.id || idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded" data-testid={`part-row-${idx}`}>
                  <input
                    type="text"
                    value={p.description}
                    onChange={e => updatePart(idx, 'description', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-sm border rounded"
                    placeholder="Part description"
                  />
                  <div className="flex items-center gap-1 w-16">
                    <span className="text-xs text-gray-400">Qty</span>
                    <input
                      type="number"
                      min="1"
                      value={p.quantity}
                      onChange={e => updatePart(idx, 'quantity', parseInt(e.target.value) || 1)}
                      className="w-10 px-1 py-1.5 text-sm border rounded text-center"
                    />
                  </div>
                  <div className="flex items-center gap-1 w-24">
                    <span className="text-xs text-gray-400">Price</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={p.unit_price}
                      onChange={e => updatePart(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                      className="w-16 px-2 py-1.5 text-sm border rounded text-center"
                    />
                  </div>
                  <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={p.is_mandatory}
                      onChange={e => updatePart(idx, 'is_mandatory', e.target.checked)}
                      className="rounded"
                    />
                    Required
                  </label>
                  <button onClick={() => removePart(idx)} className="p-1 text-gray-400 hover:text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Consumables */}
        <section className="bg-white border rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-800">Consumables</h2>
            <button onClick={addConsumable} className="flex items-center gap-1 text-xs text-blue-600 hover:underline" data-testid="add-consumable-btn">
              <Plus className="w-3.5 h-3.5" /> Add Consumable
            </button>
          </div>
          {form.consumables.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No consumables</p>
          ) : (
            <div className="space-y-2">
              {form.consumables.map((c, idx) => (
                <div key={c.id || idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                  <input
                    type="text"
                    value={c.description}
                    onChange={e => updateConsumable(idx, e.target.value)}
                    className="flex-1 px-2 py-1.5 text-sm border rounded"
                    placeholder="Consumable description"
                  />
                  <button onClick={() => removeConsumable(idx)} className="p-1 text-gray-400 hover:text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Notes */}
        <section className="bg-white border rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-800">Notes</h2>
            <button onClick={addNote} className="flex items-center gap-1 text-xs text-blue-600 hover:underline" data-testid="add-note-btn">
              <Plus className="w-3.5 h-3.5" /> Add Note
            </button>
          </div>
          {form.notes.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">No notes</p>
          ) : (
            <div className="space-y-2">
              {form.notes.map((n, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                  <input
                    type="text"
                    value={n.text || (typeof n === 'string' ? n : '')}
                    onChange={e => updateNote(idx, 'text', e.target.value)}
                    className="flex-1 px-2 py-1.5 text-sm border rounded"
                    placeholder="Note text"
                  />
                  <select
                    value={n.note_type || 'info'}
                    onChange={e => updateNote(idx, 'note_type', e.target.value)}
                    className="px-2 py-1.5 text-xs border rounded bg-white w-28"
                  >
                    <option value="info">Info</option>
                    <option value="warning">Warning</option>
                    <option value="requirement">Requirement</option>
                  </select>
                  <button onClick={() => removeNote(idx)} className="p-1 text-gray-400 hover:text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
